import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {FormSubmissionSortOrder} from 'nutshell-graphql-types';
import type {
    GetSubmissions as GetSubmissionsQuery,
    GetSubmissionsVariables as GetSubmissionsVariables,
    GetSubmissions_submissions_submissions_edges_node as Submission,
    FormSubmissionsInput,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../../apollo-client';

import GET_SUBMISSIONS from '../queries/get-submissions.graphql';

export const DEFAULT_PAGE_SIZE = 50;

export const useGetSubmissions = (
    input: FormSubmissionsInput
): {
    totalCount: number;
    submissions: Submission[] | null | undefined;
    isLoading: boolean;
    error: string | null | undefined;
    fetchPreviousPage: () => Promise<any>;
    fetchNextPage: () => Promise<any>;
    fetchFirstPageWithNewSort: (sort: FormSubmissionSortOrder) => Promise<any>;
} => {
    const sort = getSort(input.sort);
    const originalQueryVariables = {
        input: {
            ...input,
            first: DEFAULT_PAGE_SIZE,
            sort,
        },
    } as const;

    const [currentSort, setCurrentSort] = React.useState(sort);

    const {loading, data, error, fetchMore} = useQuery<
        GetSubmissionsQuery,
        GetSubmissionsVariables
    >(GET_SUBMISSIONS, {
        variables: originalQueryVariables,
        fetchPolicy: 'cache-and-network',
        skip: !sort,
    });

    const submissions = data ? data.submissions : null;

    const submissionsList = submissions
        ? submissions.submissions.edges.map((edge) => edge.node)
        : null;

    const pageInfo = submissions ? submissions.submissions.pageInfo : null;

    const handleFetchPreviousPage = () => {
        if (pageInfo && pageInfo.hasPreviousPage) {
            const nextRequestVariables = {
                input: {
                    ...input,
                    sort: currentSort,
                    before: pageInfo.startCursor,
                    last: DEFAULT_PAGE_SIZE,
                },
            } as const;

            const cachedData = readCachedData(nextRequestVariables);
            if (cachedData) {
                // We need to update the data stored under the original query variables so the UI rerenders.
                nutApolloClient.writeQuery({
                    query: GET_SUBMISSIONS,
                    variables: originalQueryVariables,
                    data: cachedData,
                });

                return Promise.resolve();
            }

            return fetchMore({
                variables: nextRequestVariables,
                // @ts-expect-error
                updateQuery: (
                    prev: GetSubmissionsQuery,
                    newResults: {
                        fetchMoreResult: any;
                    }
                ) => {
                    // fetchMore will not cache this data under nextRequestVariables, so we need to do this manually
                    // so future calls with the same variables can rely on the cache.
                    nutApolloClient.writeQuery({
                        query: GET_SUBMISSIONS,
                        variables: nextRequestVariables,
                        data: newResults.fetchMoreResult,
                    });

                    return newResults.fetchMoreResult;
                },
            });
        }

        return Promise.reject();
    };

    const handleFetchNextPage = () => {
        if (pageInfo && pageInfo.hasNextPage) {
            const nextRequestVariables = {
                input: {
                    ...input,
                    after: pageInfo.endCursor,
                    first: DEFAULT_PAGE_SIZE,
                    sort: currentSort,
                },
            } as const;

            const cachedData = readCachedData(nextRequestVariables);
            if (cachedData) {
                nutApolloClient.writeQuery({
                    query: GET_SUBMISSIONS,
                    variables: originalQueryVariables,
                    data: cachedData,
                });

                return Promise.resolve();
            }

            return fetchMore({
                variables: nextRequestVariables,
                // @ts-expect-error
                updateQuery: (
                    prev: GetSubmissionsQuery,
                    newResults: {
                        fetchMoreResult: GetSubmissionsQuery;
                    }
                ) => {
                    nutApolloClient.writeQuery({
                        query: GET_SUBMISSIONS,
                        variables: nextRequestVariables,
                        data: newResults.fetchMoreResult,
                    });

                    return newResults.fetchMoreResult;
                },
            });
        }

        return Promise.reject();
    };

    const handleFetchFirstPageWithNewSort = (newSort: FormSubmissionSortOrder) => {
        const newSortString = getSort(newSort);
        if (!newSortString) {
            return Promise.reject();
        }

        setCurrentSort(newSortString);

        const newRequestVariables = {
            input: {
                ...input,
                first: DEFAULT_PAGE_SIZE,
                sort: newSortString,
            },
        } as const;

        const cachedData = readCachedData(newRequestVariables);
        if (cachedData) {
            nutApolloClient.writeQuery({
                query: GET_SUBMISSIONS,
                variables: newRequestVariables,
                data: cachedData,
            });
        }

        return fetchMore({
            variables: newRequestVariables,
            // @ts-expect-error
            updateQuery: (
                prev: GetSubmissionsQuery,
                newResults: {
                    fetchMoreResult: GetSubmissionsQuery;
                }
            ) => {
                nutApolloClient.writeQuery({
                    query: GET_SUBMISSIONS,
                    variables: newRequestVariables,
                    data: newResults.fetchMoreResult,
                });

                return newResults.fetchMoreResult;
            },
        });
    };

    React.useEffect(() => {
        if (sort && !currentSort) {
            setCurrentSort(sort);
        }
    }, [sort, currentSort]);

    return {
        totalCount: submissions ? submissions.submissions.totalCount : 0,
        submissions: submissionsList,
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        fetchPreviousPage: handleFetchPreviousPage,
        fetchNextPage: handleFetchNextPage,
        fetchFirstPageWithNewSort: handleFetchFirstPageWithNewSort,
    };
};

function readCachedData(variables: GetSubmissionsVariables) {
    try {
        return nutApolloClient.readQuery({
            query: GET_SUBMISSIONS,
            variables,
        });
    } catch (_exception: any) {
        return null;
    }
}

function getSort(sort?: FormSubmissionSortOrder | null) {
    if (!sort) {
        return null;
    }

    return getSortOrderEnum(sort);
}

function getSortOrderEnum(sort: string): FormSubmissionSortOrder {
    switch (sort) {
        case 'submissionTime':
            return 'SUBMITTED_TIME_ASC';
        default:
        case '-submissionTime':
            return 'SUBMITTED_TIME_DESC';
    }
}
